// One ladder panel: click semantics (cross detection, bracket phantom mode, cancel-by-level) live here.

import { skeletonHtml, unitHtml } from './fmt';
import { MC_SETTLE, MQ_PER_CONTRACT, formatMc, formatMq, signedDollars } from '../core/money';
import { buildMinuteBars, buildTickBars } from '../core/bars';
import type { Bar, HistPoint, Trade } from '../core/engine';
import type { LadderRow, PositionRow, UiOrder, UiSnapshot } from '../core/types';
import type { FillRow } from '../core/db';
import type { ExploreEvent, ExploreMarket, VenueEngine, VenueId } from '../venue/engine';
import { escHtml, toast } from './toast';
import { getDefaultPanelMode } from './theme';

export interface PanelHost {
  engine: VenueEngine;
  accounts: () => { id: string; name: string }[];
  panels: () => Panel[];
  isStale: () => boolean;
  isStarred: (t: string) => boolean;
  toggleStar: (t: string) => void;
  onTickerChange: () => void;
  /** Simulated orders to draw on this ticker for this account (arb dry runs). */
  phantoms?: (ticker: string, accountId: string) => { priceMc: number; mq: number }[];
  /** Arb ladders: whether a click may cross. Off hides the cross prices and every click rests. */
  crossing?: () => boolean;
}

const ACTIVE = ['resting', 'open', 'partially_filled'];

// `interval` is the candle size to backfill the range: only 1, 60, 1440 are accepted by the API.
const CHART_RANGES: { id: string; label: string; ms: number; interval: 1 | 60 }[] = [
  { id: '1m', label: '1m', ms: 60_000, interval: 1 },
  { id: '15m', label: '15m', ms: 15 * 60_000, interval: 1 },
  { id: '1h', label: '1h', ms: 60 * 60_000, interval: 1 },
  { id: '6h', label: '6h', ms: 6 * 3_600_000, interval: 1 },
  { id: '1d', label: '1d', ms: 24 * 3_600_000, interval: 60 },
  { id: 'all', label: 'All', ms: 7 * 24 * 3_600_000, interval: 60 },
];

export type ChartType = 'line' | 'min' | 'tick';

const CHART_TYPES: { id: ChartType; label: string; title: string }[] = [
  { id: 'line', label: 'Line', title: 'Bid and ask over time' },
  { id: 'min', label: 'Bars', title: 'Minute OHLC bars from traded price' },
  { id: 'tick', label: 'Tick', title: 'One bar per N trades' },
];

/** Selectable bar sizes for tick mode. */
const TICK_SIZES = [10, 25, 50, 100, 250];

// Tick mode has no clock, so the lookback selector counts bars instead of time.
const TICK_LOOKBACKS = [25, 50, 100, 250, 500];

/** Rows added per Explore page. The cap bounds DOM, not results. */
const EXPLORE_PAGE = 50;
/** Above this many contracts an expanded event gets a filter box. */
const EXPLORE_FILTER_ABOVE = 30;
/** Contracts drawn under one event before "narrow the filter". */
const EXPLORE_MARKET_CAP = 200;
/** Rows whose volume is fetched lazily, past which the read budget matters more than the number. */
const EXPLORE_LAZY_VOLUME = 30;

export type ColId = 'my' | 'bid' | 'price' | 'ask' | 'queue' | 'depth' | 'vol';

// Ladder columns as data (keyed by `id`), not fixed DOM positions, so they can be reordered.
export interface ColDef {
  id: ColId;
  label: string;
  cls: string;
  width: number;
  title?: string;
  /** Drawn as a bar. */
  hist?: boolean;
  /** Cannot be hidden — the ladder is meaningless without it. */
  fixed?: boolean;
}

export const COLS: ColDef[] = [
  { id: 'my', label: 'My', cls: 'c-myq', width: 36, title: 'Your resting size at this level' },
  { id: 'bid', label: 'Bids', cls: 'c-bid', width: 72 },
  { id: 'price', label: 'Price', cls: 'c-price', width: 46, fixed: true },
  { id: 'ask', label: 'Asks', cls: 'c-ask', width: 72 },
  { id: 'queue', label: 'Queue', cls: 'c-queue', width: 36, title: 'Contracts ahead of your order' },
  { id: 'depth', label: 'Depth', cls: 'c-depth', width: 70, hist: true,
    title: 'Resting size at each level — blue bid, red ask. The bright line is your place in the queue.' },
  { id: 'vol', label: 'Vol', cls: 'c-vprof', width: 60, hist: true,
    title: "Volume traded at each price over the market's life" },
];

const COL_BY_ID = new Map(COLS.map((c) => [c.id, c]));

/** Fill-marker colour on the price cell: none, bid, ask, both. */
const FILL_STYLE: [string, string][] = [
  ['', ''],
  ['color-mix(in srgb, var(--bid-txt) 80%, transparent)', 'var(--surf2)'],
  ['color-mix(in srgb, var(--ask-txt) 80%, transparent)', 'var(--surf2)'],
  ['color-mix(in srgb, var(--bracket) 80%, transparent)', 'var(--surf2)'],
];
const BRACKET_OUTLINE = 'inset 0 0 0 1.5px color-mix(in srgb, var(--bracket) 60%, transparent)';
/** Queue-marker cache value for a row whose marker has never been written. */
const MARK_UNSET = '?';

/** One ladder row: its cells, resolved once per rebuild, and the values last written to them. */
interface RowView {
  price: number;
  div: HTMLElement;
  my: HTMLElement | null;
  bid: HTMLElement | null;
  ask: HTMLElement | null;
  queue: HTMLElement | null;
  depth: HTMLElement | null;
  bar: HTMLElement | null;
  mark: HTMLElement | null;
  /** Fill colour goes on the price cell; the bracket outline on My, or on price when My is hidden. */
  fillCell: HTMLElement | null;
  bracketCell: HTMLElement | null;
  my$: string;
  myCls$: string;
  bid$: string;
  ask$: string;
  queue$: string;
  depthSide$: '' | 'bid' | 'ask';
  barW$: string;
  markLeft$: string;
  best$: '' | 'bid' | 'ask' | 'both';
  fill$: number;
  bracket$: boolean;
}

/** Column layout is shared by every panel — you arrange it once. */
const COLS_KEY = 'kg_cols_v1';

export type PanelMode = 'explore' | 'ladder';

export class Panel {
  ticker: string | null = null;
  mode: PanelMode = 'explore';
  accountId = '';
  el: HTMLDivElement;

  /** Which exchange this ladder is on; events and layout are routed by it. */
  get venue(): VenueId {
    return this.host.engine.venue;
  }

  get venueLabel(): string {
    return this.host.engine.label;
  }

  /** Whether clicks send orders. Off, and every order path returns quietly. */
  get tradingEnabled(): boolean {
    return this.host.engine.tradingEnabled;
  }

  private selectedSizeMq: number = MQ_PER_CONTRACT;
  private orderMode: 'M' | 'B' = 'M';
  private bracketPending: { entryOrderId: string; hedgeCell: 'bid' | 'ask'; entrySide: 'yes' | 'no'; entryQtyMq: number; seen: boolean } | null = null;
  private phantomTimer: number | undefined;

  private colOrder: ColId[] = COLS.map((c) => c.id);
  private colWidths: Record<ColId, number> =
    Object.fromEntries(COLS.map((c) => [c.id, c.width])) as Record<ColId, number>;
  // Both histograms are off by default: depth duplicates the size columns, and volume costs a fetch.
  private colVisible: Record<ColId, boolean> =
    Object.fromEntries(COLS.map((c) => [c.id, !c.hist])) as Record<ColId, boolean>;
  private editMode = false;
  private lastSnapshot: UiSnapshot | null = null;
  /** Ladder rows with their cells resolved and last-written values, so a snapshot only touches what changed. */
  private rowViews: RowView[] = [];
  /** Last HTML written to the extra-stats block. */
  private lastExtraHtml = '';
  private scrolledOnce = false;
  private placing = false;

  private lastBestBid: number | null = null;
  private lastBestAsk: number | null = null;
  /** Arb ladders: where a click can cross right now. Null on a real book. */
  private lastCross: NonNullable<UiSnapshot['stats']['arbCross']> | null = null;
  private tickMc = 1;

  private orders: UiOrder[] = [];
  private pnlByAccount: Record<string, PositionRow> = {};
  private aggStats: { positionMq: number; pnlCash: number | null } | null = null;
  private fillsByAccount: Record<string, Record<number, [number, number]>> = {};
  private bracketsByAccount: Record<string, { entry: number[]; hedge: number[] }> = {};
  private fillsData: FillRow[] = [];
  private measCtx: CanvasRenderingContext2D | null = null;
  private exploreTimer: number | undefined;
  private openEvent = '';
  private exploreQuery = '';
  private exploreSeq = 0;
  private exploreHits: ExploreEvent[] = [];
  private chartView: 'ladder' | 'chart' = 'ladder';
  /** Quote history in MC, oldest first, bucketed per candle interval (not one flat series). */
  private hist = new Map<number, { pts: HistPoint[]; ms: number }>();
  /** Live top-of-book samples, appended after whatever backfill ends. */
  private live: HistPoint[] = [];
  private chartType: ChartType = 'line';
  /** Trades per bar in tick mode. */
  private tickN = 50;
  /** How many tick bars to show — tick mode's replacement for a time range. */
  private tickBack = 100;
  /** Backfilled minute bars, keyed by interval like `hist`. */
  private bars = new Map<number, { rows: Bar[]; ms: number; takenAt: number }>();
  /** Prints, oldest first: tick-mode backfill plus everything seen live. */
  private prints: Trade[] = [];
  /** How many prints the last tick backfill asked for. */
  private tradeCount = 0;
  /** Traded volume by price, for the Vol column. Empty until it is switched on. */
  private vprof = new Map<number, number>();
  private vprofMax = 0;
  private vprofTicker = '';
  private vprofLoading = false;
  private seriesTicker = '';
  private chartRaf = 0;
  private range = CHART_RANGES[2]; // 1h
  private exploreLimit = EXPLORE_PAGE;

  // `id` is a stable uuid, not a position: the account binding is persisted keyed by it.
  constructor(readonly id: string, private host: PanelHost, mode?: PanelMode) {
    this.mode = mode ?? (getDefaultPanelMode() as PanelMode);
    this.accountId = localStorage.getItem('kg_acct_' + id) || '';
    this.el = document.createElement('div');
    this.el.className = 'panel';
    this.el.innerHTML = this.template();
    this.q<HTMLElement>('.js-trading-off').style.display = this.tradingEnabled ? 'none' : '';
    this.bind();
    this.applyColWidths();
    this.refreshAcct();
    this.applyMode();
  }

  private q<T extends Element>(sel: string): T {
    return this.el.querySelector(sel) as T;
  }

  private template(): string {
    return `
<div class="p-search">
  <input class="js-sinput" type="text" autocomplete="off" spellcheck="false"/>
  <button class="p-star js-star" title="Star contract">☆</button>
  <button class="p-close js-close" title="Close panel" style="display:none">✕</button>
  <button class="p-clear js-clear" title="Clear">✕</button>
  <div class="p-seg js-seg">
    <button class="p-seg-b js-seg-b" data-mode="explore" title="Search events and contracts">Explore</button>
    <button class="p-seg-b js-seg-b" data-mode="ladder" title="Order book">Ladder</button>
  </div>
  <div class="srch-drop js-drop"></div>
</div>
<div class="p-explore js-explore">
  <div class="ex-list js-ex-list"></div>
</div>
<div class="p-body">
  <div class="p-chart js-chart">
    <div class="chart-bar">
      <div class="chart-types js-ctypes"></div>
      <div class="chart-ranges js-ranges"></div>
    </div>
    <canvas class="js-chart-cv"></canvas>
    <div class="chart-note js-chart-note"></div>
  </div>
  <div class="p-left">
    <div class="stat-box">
      <div class="st-row"><div class="st-lbl">Mid</div><div class="st-val js-mid" style="font-size:26px">—</div></div>
      <div class="st-row"><div class="st-lbl">Position</div><div class="st-val js-position">0</div></div>
      <div class="st-row"><div class="st-lbl">PnL</div><div class="st-val js-pnl">—</div></div>
      <div class="st-row"><div class="st-lbl">Volume</div><div class="st-val js-volume">0</div></div>
      <div class="js-st-extra" style="display:contents"></div>
    </div>
    <div class="book-warn js-book-warn" style="display:none"
      title="The local book shows a bid at or above the ask, which a matched exchange cannot do — a delta frame was dropped or arrived out of order. A fresh snapshot is being pulled.">book resyncing</div>
    <div class="order-chips">
      <div class="order-chip order-chip-bid js-bid-count" title="Resting bids">0</div>
      <div class="order-chip order-chip-ask js-ask-count" title="Resting asks">0</div>
    </div>
    <div class="p-trading-off js-trading-off" style="display:none" title="Every order path is wired; this venue just does not send yet.">Display only — orders off for ${escHtml(this.host.engine.label)}</div>
    <div style="display:flex;gap:4px">
      <button class="cancel-all-btn js-cancel-all" style="flex:1" title="Cancel this account's orders on this contract">Cxl</button>
      <button class="tb-btn js-fills-toggle" style="flex:1;font-size:11px;padding:3px 6px" title="Fill tracker">Fills</button>
    </div>
    <div class="mode-row">
      <input type="number" class="js-size-input sz-input" value="1" min="0" step="1"/>
      <button class="mode-btn js-mode active" data-mode="M" title="Manual order">M</button>
      <button class="mode-btn bmode js-mode" data-mode="B" title="Bracket order">B</button>
    </div>
    <div class="sz-grid">
      ${[1, 2, 5, 10].map((s) => `<button class="sz-btn${s === 1 ? ' active' : ''}" data-sz="${s}">${s}</button>`).join('')}
      <button class="sz-btn sz-btn-zero" data-sz="0">0</button>
    </div>
    <div style="display:flex;gap:4px">
      <button class="tb-btn js-edit-toggle" style="flex:1;font-size:10px;padding:3px 6px" title="Edit columns">Edit</button>
      <button class="tb-btn js-autofit" style="flex:1;font-size:10px;padding:3px 6px" title="Auto-fit columns to content">Autofit</button>
    </div>
    <div class="col-menu js-col-menu" style="display:none"></div>
    <div>
      <button class="tb-btn js-acct-btn" title="Trading account"
        style="width:100%;font-size:10px;padding:3px 6px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">Select account</button>
    </div>
    <div class="view-row">
      <button class="view-btn js-view on" data-view="ladder" title="Order book">Ladder</button>
      <button class="view-btn js-view" data-view="chart" title="Bid/ask over time, aligned to the book">Chart</button>
    </div>
  </div>
  <div class="p-right" style="position:relative">
    <div class="ldr-head"></div>
    <div class="ldr-body js-ladder"><div class="p-empty">Search to load</div></div>
    <div class="fills-tracker js-fills-tracker">
      <div class="fills-drag js-fills-drag" title="drag to resize"></div>
      <div class="fills-head">
        <span style="flex:0 0 58px">Time</span><span style="flex:0 0 40px">Side</span>
        <span style="flex:0 0 46px;text-align:right">Price</span><span style="flex:0 0 40px;text-align:right">Size</span>
        <span style="flex:1;text-align:right">Liq</span>
      </div>
      <div class="fills-list js-fills-list"></div>
    </div>
    <div class="mkt-closed-overlay js-closed-overlay" style="display:none">
      <span class="mkt-closed-label">Market Closed</span>
      <span class="mkt-result js-mkt-result" style="display:none"></span>
    </div>
  </div>
</div>`;
  }

  private bind(): void {
    const sinput = this.q<HTMLInputElement>('.js-sinput');
    const drop = this.q<HTMLDivElement>('.js-drop');
    const clear = this.q<HTMLButtonElement>('.js-clear');
    let searchTimer: number | undefined;

    this.q('.js-star').addEventListener('click', () => {
      if (this.ticker) this.host.toggleStar(this.ticker);
    });
    this.q('.js-close').addEventListener('click', () => this.clearTicker());

    // The column header is a separate grid from the rows; sync horizontal scroll so labels stay aligned.
    const ldr = this.q<HTMLElement>('.js-ladder');
    ldr.addEventListener('scroll', () => {
      this.q<HTMLElement>('.ldr-head').scrollLeft = ldr.scrollLeft;
    }, { passive: true });

    this.q('.js-cancel-all').addEventListener('click', async () => {
      if (!this.ticker || !this.tradingEnabled) return;
      if (!this.accountId
        && !confirm(`No account selected — this cancels ALL orders on ${this.ticker}, including other accounts'. Proceed?`)) {
        return;
      }
      const r = await this.host.engine.cancelAllOrders(this.ticker, this.accountId || undefined);
      if (!r.ok) {
        toast(`CANCEL FAILED — ${r.failed} order(s) MAY STILL BE RESTING`
          + (r.cancelled ? ` (${r.cancelled} cancelled)` : '')
          + (r.error ? `: ${r.error}` : ''), 'error');
      } else toast(`cancelled ${r.cancelled}`, 'ok');
    });

    for (const b of this.el.querySelectorAll<HTMLElement>('.js-view')) {
      b.addEventListener('click', () => this.setChartView(b.dataset.view as 'ladder' | 'chart'));
    }

    const types = this.q<HTMLElement>('.js-ctypes');
    types.addEventListener('click', (ev) => {
      const b = (ev.target as HTMLElement).closest<HTMLElement>('[data-ct]');
      if (b) { this.setChartType(b.dataset.ct as ChartType); return; }
      const n = (ev.target as HTMLElement).closest<HTMLElement>('[data-tn]');
      if (!n) return;
      // Cycle the bar size rather than spend width on five more buttons.
      const i = TICK_SIZES.indexOf(this.tickN);
      this.tickN = TICK_SIZES[(i + 1) % TICK_SIZES.length]!;
      this.renderChartBar();
      this.scheduleChart();
    });
    this.renderChartBar();

    const ranges = this.q<HTMLElement>('.js-ranges');
    this.renderRanges();
    ranges.addEventListener('click', (ev) => {
      const b = (ev.target as HTMLElement).closest<HTMLElement>('.rg-btn');
      if (!b) return;
      if (this.chartType === 'tick') {
        this.tickBack = Number(b.dataset.rg) || this.tickBack;
      } else {
        this.range = CHART_RANGES.find((r) => r.id === b.dataset.rg) ?? this.range;
      }
      for (const x of ranges.querySelectorAll('.rg-btn')) x.classList.toggle('on', x === b);
      void this.loadHistory();
      this.scheduleChart();
    });
    // The chart maps price to the ladder's own row positions, so redraw it when the ladder scrolls.
    this.q<HTMLElement>('.js-ladder').addEventListener('scroll', () => this.scheduleChart(), { passive: true });

    for (const b of this.el.querySelectorAll<HTMLElement>('.js-seg-b')) {
      b.addEventListener('click', () => this.setMode(b.dataset.mode as PanelMode));
    }

    // One input, two jobs: event-title search in Explore, ticker box in Ladder.
    sinput.addEventListener('input', () => {
      clearTimeout(searchTimer);
      clearTimeout(this.exploreTimer);
      const q = sinput.value.trim();
      clear.style.display = q ? 'block' : 'none';
      if (this.mode === 'explore') {
        this.exploreTimer = window.setTimeout(() => this.renderExplore(q), 160);
        return;
      }
      if (q.length < 2) {
        drop.style.display = 'none';
        return;
      }
      searchTimer = window.setTimeout(() => void this.search(q, drop), 280);
    });
    sinput.addEventListener('blur', () => window.setTimeout(() => { drop.style.display = 'none'; }, 180));
    clear.addEventListener('click', () => {
      sinput.value = '';
      clear.style.display = 'none';
      drop.style.display = 'none';
      this.clearTicker();
    });

    // size input + buttons
    const szInput = this.q<HTMLInputElement>('.js-size-input');
    const syncActive = (contracts: number) => {
      this.el.querySelectorAll<HTMLElement>('.sz-btn').forEach((b) => b.classList.remove('active'));
      const match = [...this.el.querySelectorAll<HTMLElement>('.sz-btn')]
        .find((b) => Number(b.dataset.sz) === contracts);
      match?.classList.add('active');
    };
    szInput.addEventListener('input', () => {
      const v = parseInt(szInput.value, 10);
      if (!Number.isNaN(v) && v >= 0) {
        this.selectedSizeMq = v * MQ_PER_CONTRACT;
        syncActive(v);
      }
    });
    this.el.querySelectorAll<HTMLElement>('.sz-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const v = Number(btn.dataset.sz);
        this.selectedSizeMq = v * MQ_PER_CONTRACT;
        szInput.value = String(v);
        syncActive(v);
      });
      // Right-click accumulates, so 5 + 5 + 2 gets you to 12 without typing.
      btn.addEventListener('contextmenu', (ev) => {
        ev.preventDefault();
        this.selectedSizeMq += Number(btn.dataset.sz) * MQ_PER_CONTRACT;
        const contracts = this.selectedSizeMq / MQ_PER_CONTRACT;
        szInput.value = String(contracts);
        syncActive(contracts);
      });
    });

    const editBtn = this.q<HTMLButtonElement>('.js-edit-toggle');
    const colMenu = this.q<HTMLElement>('.js-col-menu');
    editBtn.addEventListener('click', () => {
      this.editMode = !this.editMode;
      this.q('.p-right').classList.toggle('edit-mode', this.editMode);
      editBtn.style.borderColor = this.editMode ? 'var(--accent)' : '';
      editBtn.style.color = this.editMode ? 'var(--accent)' : '';
      colMenu.style.display = this.editMode ? 'flex' : 'none';
      this.renderColHead();   // drag handles only exist while editing
    });
    this.q('.js-autofit').addEventListener('click', () => this.autoResizeCols());
    this.loadCols();
    this.renderColMenu();
    this.renderColHead();

    const ldrBody = this.q<HTMLElement>('.js-ladder');
    const ldrHead = this.q<HTMLElement>('.ldr-head');
    ldrBody.addEventListener('scroll', () => { ldrHead.scrollLeft = ldrBody.scrollLeft; });

    this.q('.js-fills-toggle').addEventListener('click', () => this.toggleFills());
    this.q('.js-fills-drag').addEventListener('mousedown', (e) => {
      e.preventDefault();
      const t = this.q<HTMLElement>('.js-fills-tracker');
      const startY = (e as MouseEvent).clientY;
      const startH = t.offsetHeight;
      const onMove = (ev: MouseEvent) => {
        t.style.height = Math.max(80, Math.min(520, startH + (startY - ev.clientY))) + 'px';
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });

    this.bindLadderClicks(ldrBody);

    this.el.querySelectorAll<HTMLElement>('.js-mode').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.orderMode = btn.dataset.mode as 'M' | 'B';
        this.el.querySelectorAll<HTMLElement>('.js-mode')
          .forEach((b) => b.classList.toggle('active', b.dataset.mode === this.orderMode));
        if (this.orderMode !== 'B') this.cancelPhantom();
      });
    });
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && this.bracketPending) {
        this.cancelPhantom();
        toast('Bracket canceled — entry left as a normal order.');
      }
    });

    this.q('.js-acct-btn').addEventListener('click', (ev) => {
      ev.stopPropagation();
      this.openAcctMenu();
    });
  }

  private bindLadderClicks(ladder: HTMLElement): void {
    ladder.addEventListener('click', (ev) => {
      if (!this.ticker) return;
      const target = ev.target as HTMLElement;
      const row = target.closest('.lrow');
      if (!row) return;
      const priceEl = row.querySelector<HTMLElement>('.c-price');
      if (!priceEl) return;
      const price = Number(priceEl.dataset.price);

      // Cmd/Ctrl-click a My cell -> per-order popup, so orders at one price can be cancelled individually.
      const myq = target.closest('.c-myq');
      if (myq && (ev.metaKey || ev.ctrlKey)) {
        this.showLevelOrders(myq as HTMLElement, price);
        return;
      }

      const isBid = !!target.closest('.c-bid');
      const isAsk = !!target.closest('.c-ask');
      if (!isBid && !isAsk) return;
      // A venue that cannot trade yet: the click is wired, and does nothing.
      if (!this.tradingEnabled) return;

      // No order placement on stale data — the ladder you're clicking may not be the book any more.
      if (this.host.isStale()) {
        toast('DISCONNECTED — data stale, order not sent', 'error');
        return;
      }
      // No same-cell debounce; placeOrder's per-panel in-flight lock prevents a double-click double-submitting.
      const bestBid = this.lastBestBid;
      const bestAsk = this.lastBestAsk;

      if (this.orderMode === 'B') {
        void this.bracketClick(isBid, isAsk, price);
        return;
      }
      // On a real book a cross is the other side's best; on an arb ladder it's the cross price, gated by crossing.
      const cross = this.lastCross;
      const crossing = !this.host.crossing || this.host.crossing();
      const takeBuyAt = cross ? (crossing ? cross.buyMc : null) : bestAsk;
      const takeSellAt = cross ? (crossing ? cross.sellMc : null) : bestBid;
      if (isBid) {
        // Clicking at or above where YES can be taken is a cross: buy aggressively.
        if (takeBuyAt !== null && price >= takeBuyAt) void this.placeOrder(takeBuyAt, 'yes', 'fok');
        else void this.placeOrder(price, 'yes', 'post');
      } else {
        // Ask cell — selling YES is buying NO at (1000 - price).
        if (takeSellAt !== null && price <= takeSellAt) void this.placeOrder(MC_SETTLE - takeSellAt, 'no', 'fok');
        else void this.placeOrder(MC_SETTLE - price, 'no', 'post');
      }
    });

    ladder.addEventListener('dblclick', (ev) => {
      const target = ev.target as HTMLElement;
      if (target.closest('.c-bid') || target.closest('.c-ask')) return;
      if (target.closest('.lrow')) this.centerSpread();
    });

    // Right-click a My cell -> cancel this account's orders at that level.
    ladder.addEventListener('contextmenu', async (ev) => {
      ev.preventDefault();
      if (!this.ticker || !this.tradingEnabled) return;
      const cell = (ev.target as HTMLElement).closest('.c-myq');
      if (!cell) return;
      const priceEl = cell.closest('.lrow')?.querySelector<HTMLElement>('.c-price');
      if (!priceEl) return;
      const r = await this.host.engine.cancelLevel(
        this.ticker, Number(priceEl.dataset.price), this.accountId || undefined);
      if (!r.ok && r.error) toast('CANCEL FAILED — orders may still be resting: ' + r.error, 'error');
      else if (r.failed > 0) toast(`cancel level: ${r.failed} cancel(s) FAILED — orders may still be resting`, 'error');
    });
  }

  setMode(mode: PanelMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.applyMode();
    this.host.onTickerChange(); // persists the layout
  }

  private applyMode(): void {
    const explore = this.mode === 'explore';
    this.q<HTMLElement>('.js-explore').style.display = explore ? 'flex' : 'none';
    this.q<HTMLElement>('.p-body').style.display = explore ? 'none' : 'flex';
    for (const b of this.el.querySelectorAll<HTMLElement>('.js-seg-b')) {
      b.classList.toggle('on', b.dataset.mode === this.mode);
    }
    const input = this.q<HTMLInputElement>('.js-sinput');
    input.placeholder = explore ? this.host.engine.hints.explore : this.host.engine.hints.ladder;
    if (explore) {
      this.q<HTMLElement>('.js-drop').style.display = 'none';
      void this.renderExplore(input.value.trim());
    }
  }

  /** Event hits for the query, or a state line when there is nothing to show. */
  private async renderExplore(q: string): Promise<void> {
    const list = this.q<HTMLElement>('.js-ex-list');
    const { building, count } = this.host.engine.exploreState();
    const hints = this.host.engine.hints;

    // A new query starts from the first page again.
    if (q !== this.exploreQuery) {
      this.exploreQuery = q;
      this.exploreLimit = EXPLORE_PAGE;
    }

    if (q.length < 2) {
      list.innerHTML = `<div class="ex-note">${building
        ? 'Building the event index…'
        : count === null
          ? hints.exploreEmpty
          : count
            ? `Type to search ${count.toLocaleString()} open events.`
            : 'Event index unavailable — search by ticker in Ladder mode.'}</div>`;
      return;
    }
    const seq = ++this.exploreSeq;
    if (count === null && !this.exploreHits.length) list.innerHTML = skeletonHtml(6);
    let hits: ExploreEvent[];
    let total: number;
    try {
      ({ hits, total } = await this.host.engine.searchEvents(q, this.exploreLimit));
    } catch (e) {
      if (seq !== this.exploreSeq) return;
      list.innerHTML = `<div class="ex-note err">Search failed: ${escHtml((e as Error).message)}</div>`;
      return;
    }
    if (seq !== this.exploreSeq) return; // a newer query has taken over
    this.exploreHits = hits;
    if (!hits.length) {
      list.innerHTML = `<div class="ex-note">No events match “${escHtml(q)}”.${
        building ? ' The index is still building.' : ''}</div>`;
      return;
    }
    // The series line is not decoration: it's often why a query matched this event at all.
    list.innerHTML = hits.map((e) => `
      <div class="ex-ev" data-ev="${escHtml(e.id)}">
        <div class="ex-ev-hd">
          <span class="ex-ev-t">${escHtml(e.title)}</span>
          <span class="ex-ev-c">${escHtml(e.tag)}</span>
        </div>
        <div class="ex-ev-sub">${e.series ? `<span class="ex-ev-ser">${escHtml(e.series)}</span> · ` : ''}${e.sub ? `${escHtml(e.sub)} · ` : ''}
          <span class="mono">${escHtml(e.id)}</span></div>
        <div class="ex-mkts" style="display:none"></div>
      </div>`).join('')
      + (total > hits.length
        ? `<button class="ex-more js-ex-more">Load ${
            Math.min(EXPLORE_PAGE, total - hits.length)} more <span class="ex-more-n">${
            (total - hits.length).toLocaleString()} remaining</span></button>`
        : total > EXPLORE_PAGE
          ? `<div class="ex-note">All ${total.toLocaleString()} matches shown.</div>`
          : '');

    // Preserve scroll position: re-rendering replaces the list and would otherwise reset it.
    this.q<HTMLElement>('.js-ex-more')?.addEventListener('click', () => {
      const keep = list.scrollTop;
      this.exploreLimit += EXPLORE_PAGE;
      void this.renderExplore(q).then(() => { list.scrollTop = keep; });
    });

    for (const row of list.querySelectorAll<HTMLElement>('.ex-ev')) {
      row.querySelector('.ex-ev-hd')!.addEventListener('click', () => void this.toggleEvent(row));
      row.querySelector('.ex-ev-sub')!.addEventListener('click', () => void this.toggleEvent(row));
    }
    // Reopen whatever was open before the re-render, so typing does not collapse it.
    const keep = list.querySelector<HTMLElement>(`.ex-ev[data-ev="${CSS.escape(this.openEvent)}"]`);
    if (this.openEvent && keep) void this.toggleEvent(keep);
  }

  private async toggleEvent(row: HTMLElement): Promise<void> {
    const box = row.querySelector<HTMLElement>('.ex-mkts')!;
    const ev = row.dataset.ev ?? '';
    if (box.style.display !== 'none') {
      box.style.display = 'none';
      this.openEvent = '';
      return;
    }
    this.openEvent = ev;
    box.style.display = 'block';
    box.innerHTML = skeletonHtml(3);
    let markets: ExploreMarket[] = [];
    try {
      markets = await this.host.engine.eventMarkets(ev);
    } catch (e) {
      box.innerHTML = `<div class="ex-note err">Could not load contracts: ${escHtml((e as Error).message)}</div>`;
      return;
    }
    if (this.openEvent !== ev) return; // collapsed or switched while loading
    const live = markets.filter((m) => !['settled', 'resolved', 'finalized'].includes(m.status || 'open'));
    if (!live.length) {
      box.innerHTML = '<div class="ex-note">No open contracts in this event.</div>';
      return;
    }
    // Past a threshold the table gets a filter box instead of a different panel layout.
    const filter = live.length > EXPLORE_FILTER_ABOVE
      ? `<input class="ex-filter js-ex-filter" type="text" placeholder="filter ${live.length.toLocaleString()} contracts…" autocomplete="off" spellcheck="false"/>`
      : '';
    box.innerHTML = `${filter}
      <div class="ex-mh"><span>Contract</span><span>Bid</span><span>Ask</span><span>Volume</span></div><div class="js-ex-rows"></div>`;
    const rows = box.querySelector<HTMLElement>('.js-ex-rows')!;
    const paint = (words: string[]) => this.renderEventMarkets(live, words, rows);
    paint([]);
    box.querySelector<HTMLInputElement>('.js-ex-filter')?.addEventListener('input', (e) => {
      paint((e.target as HTMLInputElement).value.toLowerCase().split(/\s+/).filter(Boolean));
    });
  }

  /** One row per contract; volume filled in afterwards when the venue lists none. */
  private renderEventMarkets(all: ExploreMarket[], words: string[], rows: HTMLElement): void {
    const hits = words.length
      ? all.filter((m) => { const hay = `${m.title} ${m.ticker}`.toLowerCase(); return words.every((w) => hay.includes(w)); })
      : all;
    const shown = hits.slice(0, EXPLORE_MARKET_CAP);
    rows.innerHTML = shown.map((m, i) => `
      <div class="ex-mk" data-tk="${escHtml(m.ticker)}" title="${escHtml(m.title)}">
        <span class="ex-mk-t">${escHtml(m.title)}${m.kind ? `<span class="ex-mk-kind">${escHtml(m.kind)}</span>` : ''}</span>
        <span class="ex-mk-b mono">${m.bidMc !== null ? unitHtml(`${formatMc(m.bidMc, 1)}¢`) : '—'}</span>
        <span class="ex-mk-a mono">${m.askMc !== null ? unitHtml(`${formatMc(m.askMc, 1)}¢`) : '—'}</span>
        <span class="ex-mk-v mono js-vol">${m.volume !== null ? compact(m.volume) : i < EXPLORE_LAZY_VOLUME ? '…' : '—'}</span>
      </div>`).join('')
      + (hits.length > shown.length ? `<div class="ex-note">${(hits.length - shown.length).toLocaleString()} more — narrow the filter.</div>` : '')
      + (!hits.length ? '<div class="ex-note">Nothing in this event matches.</div>' : '');

    const lazyVol = this.host.engine.marketVolume?.bind(this.host.engine);
    let lazyLeft = EXPLORE_LAZY_VOLUME;
    for (const mk of rows.querySelectorAll<HTMLElement>('.ex-mk')) {
      const tk = mk.dataset.tk!;
      mk.addEventListener('click', () => {
        this.q<HTMLInputElement>('.js-sinput').value = tk;
        this.select(tk);
      });
      const m = shown.find((x) => x.ticker === tk);
      if (m && m.volume === null && lazyVol && lazyLeft-- > 0) {
        void lazyVol(tk).then((mq) => {
          if (!mk.isConnected) return;
          mk.querySelector('.js-vol')!.textContent = mq === null ? '—' : compact(mq / MQ_PER_CONTRACT);
        });
      }
    }
  }

  private async search(q: string, drop: HTMLElement): Promise<void> {
    drop.style.display = 'none';
    const markets = await this.host.engine.searchMarkets(q);
    if (!markets.length) return;
    drop.innerHTML = markets.map((m) => `
      <div class="srch-item" data-ticker="${escHtml(m.ticker)}" data-title="${escHtml(m.title)}">
        <span class="si-tk">${escHtml(m.ticker)}</span>
        <span class="si-title">${escHtml(m.title)}</span>
      </div>`).join('');
    drop.querySelectorAll<HTMLElement>('.srch-item').forEach((item) => {
      item.addEventListener('mousedown', () => {
        this.select(item.dataset.ticker!);
        this.q<HTMLInputElement>('.js-sinput').value = item.dataset.ticker!;
        drop.style.display = 'none';
      });
    });
    drop.style.display = 'block';
  }

  /** Refused: the same contract on the same account open in two panels would fight over one order set. */
  private contractTaken(ticker: string, accountId: string): boolean {
    if (!accountId) return false; // no account selected -> viewing only
    return this.host.panels().some((p) => p !== this && p.ticker === ticker && p.accountId === accountId);
  }

  select(ticker: string): void {
    if (this.contractTaken(ticker, this.accountId)) {
      toast(`${ticker} is already open on this account in another panel.`);
      this.q<HTMLInputElement>('.js-sinput').value = this.ticker || '';
      return;
    }
    const old = this.ticker;
    if (old) this.host.engine.releaseTicker(old);
    this.ticker = ticker;
    this.q<HTMLInputElement>('.js-sinput').value = ticker;
    this.scrolledOnce = false;
    this.resetTickerCaches();
    this.resetStats();
    this.host.engine.subscribe(ticker);
    this.resetHistory();
    this.vprof.clear();
    this.vprofMax = 0;
    this.vprofTicker = '';
    if (this.colVisible.vol) void this.loadVolProfile();
    if (this.chartView === 'chart') void this.loadHistory();
    this.syncStar();
    this.setMode('ladder');
    this.host.onTickerChange();
  }

  clearTicker(): void {
    const old = this.ticker;
    this.ticker = null;
    this.scrolledOnce = false;
    this.resetTickerCaches();
    this.resetStats();
    this.q<HTMLElement>('.js-closed-overlay').style.display = 'none';
    this.q<HTMLElement>('.js-fills-tracker').style.display = 'none';
    this.q<HTMLElement>('.js-ladder').innerHTML = '<div class="p-empty">Search to load</div>';
    this.q<HTMLInputElement>('.js-sinput').value = '';
    this.syncStar();
    if (old) this.host.engine.releaseTicker(old);
    this.host.onTickerChange();
  }

  /** Drops every per-ticker cache so a panel can't act on the previous ticker's orders/fills/brackets. */
  private resetTickerCaches(): void {
    this.orders = [];
    this.pnlByAccount = {};
    this.aggStats = null;
    this.fillsByAccount = {};
    this.bracketsByAccount = {};
    this.fillsData = [];
    this.lastBestBid = null;
    this.lastBestAsk = null;
    this.lastCross = null;
    this.cancelPhantom();
    document.getElementById('kg-order-pop')?.remove();
  }

  private resetStats(): void {
    this.q('.js-mid').textContent = '—';
    this.q('.js-position').textContent = '0';
    this.q('.js-pnl').textContent = '—';
    this.q('.js-volume').textContent = '0';
    this.q('.js-bid-count').textContent = '0';
    this.q('.js-ask-count').textContent = '0';
  }

  syncStar(): void {
    const btn = this.q<HTMLElement>('.js-star');
    const close = this.q<HTMLElement>('.js-close');
    if (!this.ticker) {
      btn.style.display = 'none';
      close.style.display = 'none';
      return;
    }
    btn.style.display = 'block';
    close.style.display = 'block';
    // With a contract loaded the close ✕ is the only ✕ — hide the search clear.
    this.q<HTMLElement>('.js-clear').style.display = 'none';
    const starred = this.host.isStarred(this.ticker);
    btn.textContent = starred ? '★' : '☆';
    btn.classList.toggle('starred', starred);
  }

  /** Repaint from the last snapshot: a phantom changed, nothing on the feed did. */
  refresh(): void {
    if (this.lastSnapshot) this.onSnapshot(this.lastSnapshot);
  }

  onSnapshot(msg: UiSnapshot): void {
    // Kept so a column change can repaint immediately instead of waiting for the next feed frame.
    this.lastSnapshot = msg;
    msg = this.overlayPhantoms(msg);
    msg = this.hideCrossing(msg);
    this.lastCross = msg.stats.arbCross ?? null;
    const stats = msg.stats;
    this.tickMc = stats.tickMc;
    this.setHtml('.js-mid', unitHtml(stats.midYesDeci !== null ? (stats.midYesDeci / 100).toFixed(1) + '¢' : '—'));
    const extra = (stats.extra ?? []).map((x) =>
      `<div class="st-row"><div class="st-lbl">${escHtml(x.label)}</div><div class="st-val" style="font-size:12px${x.color ? `;color:${x.color}` : ''}">${escHtml(x.value)}</div></div>`).join('');
    if (extra !== this.lastExtraHtml) {
      this.q<HTMLElement>('.js-st-extra').innerHTML = extra;
      this.lastExtraHtml = extra;
    }

    this.pnlByAccount = msg.pnlByAccount;
    this.aggStats = { positionMq: stats.positionMq, pnlCash: stats.pnlCash };
    this.orders = msg.orders;
    this.applyAccountStats();

    this.setText('.js-bid-count', String(stats.bidOrders));
    this.setText('.js-ask-count', String(stats.askOrders));
    this.setText('.js-volume', String(stats.volume));

    // Crossed means our copy of the book is wrong, not the market — say so rather than a blank mid.
    this.q<HTMLElement>('.js-book-warn').style.display = stats.crossed ? 'block' : 'none';

    this.pushSample(stats.bestYesBid, stats.bestYesAsk);

    const closed = ['settled', 'finalized', 'resolved'].includes(stats.marketStatus);
    const overlay = this.q<HTMLElement>('.js-closed-overlay');
    overlay.style.display = closed ? 'flex' : 'none';
    if (closed) {
      const res = stats.marketResult === 'yes' || stats.marketResult === 'no' ? stats.marketResult : '';
      overlay.querySelector('.mkt-closed-label')!.textContent = 'Market Resolved';
      const tag = overlay.querySelector<HTMLElement>('.js-mkt-result')!;
      tag.style.display = res ? '' : 'none';
      tag.className = `mkt-result js-mkt-result ${res}`;
      tag.textContent = res.toUpperCase();
    }

    this.fillsByAccount = msg.fills;
    this.bracketsByAccount = msg.brackets;

    // If the pending bracket entry is no longer resting, exit phantom mode.
    if (this.bracketPending) {
      const o = msg.orders.find((x) => x.order_id === this.bracketPending!.entryOrderId);
      const active = !!o && ACTIVE.includes(o.status);
      if (active) this.bracketPending.seen = true;
      else if (this.bracketPending.seen) {
        this.cancelPhantom();
        toast('Bracket entry no longer resting — bracket canceled.');
      }
    }

    this.renderLadder(msg);
  }

  private applyAccountStats(): void {
    const agg = this.aggStats ?? { positionMq: 0, pnlCash: null };
    let posMq: number;
    let pnl: number | null;
    // A venue with no per-account ledger sends no per-account rows; fall back to the aggregate.
    if (this.accountId && Object.keys(this.pnlByAccount).length) {
      const row = this.pnlByAccount[this.accountId];
      posMq = row ? row.positionMq : 0;
      pnl = row ? row.pnlCash : null;
    } else {
      posMq = agg.positionMq;
      pnl = agg.pnlCash;
    }
    this.setText('.js-position', posMq !== 0 ? formatMq(posMq) : '0');
    const pnlEl = this.q<HTMLElement>('.js-pnl');
    this.setHtml('.js-pnl', unitHtml(pnl === null ? '—' : signedDollars(pnl)));
    const color = pnl === null ? '' : pnl > 0 ? 'var(--ok)' : pnl < 0 ? 'var(--bad)' : '';
    if (pnlEl.style.color !== color) pnlEl.style.color = color;
  }

  /** With arb crossing off, takes the cross sizes back out of the bid/ask columns; snapshot itself untouched. */
  private hideCrossing(msg: UiSnapshot): UiSnapshot {
    const x = msg.stats.arbCross;
    if (!x || !this.host.crossing || this.host.crossing()) return msg;
    const rows = msg.rows.map((r) => {
      const buy = x.buyMc !== null && r[2] === x.buyMc ? x.buyMq : 0;
      const sell = x.sellMc !== null && r[2] === x.sellMc ? x.sellMq : 0;
      if (!buy && !sell) return r;
      const c = [...r] as LadderRow;
      c[1] = Math.max(0, c[1] - buy);
      c[3] = Math.max(0, c[3] - sell);
      return c;
    });
    return { ...msg, rows };
  }

  /** Draws simulated orders into the My column; phantom-only levels use the accent color, never the real one. */
  private overlayPhantoms(msg: UiSnapshot): UiSnapshot {
    const ph = this.ticker && this.host.phantoms ? this.host.phantoms(this.ticker, this.accountId) : [];
    if (!ph.length) return msg;
    const rows = msg.rows.map((r) => [...r] as LadderRow);
    const at = new Map(rows.map((r, i) => [r[2], i]));
    for (const p of ph) {
      const i = at.get(p.priceMc);
      if (i === undefined) continue;
      const r = rows[i];
      r[0] += p.mq;
      r[5] += 1;
      if (!r[6]) r[6] = 'p';
    }
    return { ...msg, rows };
  }

  private renderLadder(msg: UiSnapshot): void {
    const ladder = this.q<HTMLElement>('.js-ladder');
    const rows = msg.rows;
    const bestBid = msg.stats.bestYesBid;
    const bestAsk = msg.stats.bestYesAsk;
    this.lastBestBid = bestBid;
    this.lastBestAsk = bestAsk;

    const tick = this.tickMc;
    const decimals = tick === 10 ? 0 : 1;
    // A synthetic (arb) ladder runs symmetric about zero instead of 1..99¢, per its own snapshot.
    const firstPrice = msg.stats.priceHiMc ?? MC_SETTLE - tick;
    const lastPrice = msg.stats.priceLoMc ?? tick;
    const expected = Math.round((firstPrice - lastPrice) / tick) + 1;
    const rangeSig = `${firstPrice}:${lastPrice}`;

    const rowMap = new Map<number, LadderRow>();
    for (const r of rows) rowMap.set(r[2], r);

    // Rebuild the DOM skeleton only when the tick, price grid or column layout changes.
    if (Number(ladder.dataset.tick) !== tick || ladder.children.length !== expected
      || ladder.dataset.range !== rangeSig || ladder.dataset.cols !== this.colSig()) {
      if (!rows.length && bestBid === null && bestAsk === null) {
        const fc = ladder.firstElementChild;
        if (!fc || fc.className === 'p-empty') {
          ladder.innerHTML = '<div class="p-empty">Waiting for data…</div>';
          return;
        }
      }
      const frag = document.createDocumentFragment();
      const cols = this.visibleCols();
      const priceAt = cols.findIndex((c) => c.id === 'price');
      for (let price = firstPrice; price >= lastPrice; price -= tick) {
        const div = document.createElement('div');
        // Zero only exists on a symmetric grid (an arb): mark it as the centre.
        div.className = price === 0 ? 'lrow lrow-zero' : 'lrow';
        div.dataset.price = String(price);
        div.innerHTML = cols.map((c, ci) => {
          if (c.id === 'price') {
            return `<div class="c-price" data-price="${price}">${formatMc(price, decimals)}</div>`;
          }
          // Left-of-price histograms are anchored right and grow leftward; baked in at construction.
          return c.hist
            ? `<div class="${c.cls} c-hist${ci < priceAt ? ' hist-left' : ''}"><i></i><b></b></div>`
            : `<div class="${c.cls}"></div>`;
        }).join('');
        frag.appendChild(div);
      }
      ladder.replaceChildren(frag);
      ladder.dataset.tick = String(tick);
      ladder.dataset.range = rangeSig;
      ladder.dataset.cols = this.colSig();
      this.scrolledOnce = false;
      this.paintVolProfile();   // fresh rows carry no bars
    }

    // Fast path: compare against what each row last showed and write only the differences.
    const views = this.ensureRowViews();
    const trackDepth = views.length > 0 && views[0].depth !== null;
    // Depth bars are scaled to the deepest level in view, so the tallest bar is always full width.
    let maxDepth = 1;
    if (trackDepth) {
      for (const r of rows) maxDepth = Math.max(maxDepth, r[1], r[3]);
    }
    let bidEl: Element | null = null;
    let askEl: Element | null = null;
    for (const v of views) {
      const r = rowMap.get(v.price);
      const myQ = r ? r[0] : 0;
      const bid = r ? r[1] : 0;
      const ask = r ? r[3] : 0;
      const queue = r ? r[4] : null;

      const isBid = v.price === bestBid;
      const isAsk = v.price === bestAsk;
      if (isBid) bidEl = v.div;
      if (isAsk) askEl = v.div;
      const best = isBid && isAsk ? 'both' : isBid ? 'bid' : isAsk ? 'ask' : '';
      if (best !== v.best$) {
        v.div.classList.toggle('best-bid', isBid);
        v.div.classList.toggle('best-ask', isAsk);
        v.best$ = best;
      }

      if (v.my) {
        const text = myQ > 0 ? `${formatMq(myQ)}/${r ? r[5] : 0}` : '';
        const cls = 'c-myq' + (r && r[6] ? ' side-' + r[6] : '');
        if (text !== v.my$) { v.my.textContent = text; v.my$ = text; }
        if (cls !== v.myCls$) { v.my.className = cls; v.myCls$ = cls; }
      }
      if (v.bid) {
        const text = bid ? formatMq(bid) : '';
        if (text !== v.bid$) { v.bid.textContent = text; v.bid$ = text; }
      }
      if (v.ask) {
        const text = ask ? formatMq(ask) : '';
        if (text !== v.ask$) { v.ask.textContent = text; v.ask$ = text; }
      }
      if (v.queue) {
        const text = myQ > 0 && queue !== null ? String(queue) : '';
        if (text !== v.queue$) { v.queue.textContent = text; v.queue$ = text; }
      }

      // Depth: bid size in the bid hue, ask size in the ask hue, scaled to the deepest level on screen.
      if (v.depth && v.bar && v.mark) {
        const size = bid || ask;
        const side = bid > 0 ? 'bid' : ask > 0 ? 'ask' : '';
        if (side !== v.depthSide$) {
          v.depth.classList.toggle('side-bid', side === 'bid');
          v.depth.classList.toggle('side-ask', side === 'ask');
          v.depthSide$ = side;
        }
        const w = size ? `${Math.max(2, (size / maxDepth) * 100)}%` : '0';
        if (w !== v.barW$) { v.bar.style.width = w; v.barW$ = w; }
        // `queue` is the size ahead of us in contracts; convert to milli-contracts to match level size.
        const left = myQ > 0 && queue !== null && size > 0
          ? `${Math.min(100, ((queue * MQ_PER_CONTRACT) / size) * 100)}%` : '';
        if (left !== v.markLeft$) {
          v.mark.style.display = left ? 'block' : 'none';
          if (left) v.mark.style.left = left;
          v.markLeft$ = left;
        }
      }
    }

    this.applyFillColors();
    this.applyBracketOutlines();

    if (!this.scrolledOnce && (bidEl || askEl)) {
      this.scrolledOnce = true;
      requestAnimationFrame(() => this.centerSpread());
    }
  }

  /** Re-centers the spread, since that's a one-shot calculation that drifts off-center when resized. */
  onResized(): void {
    if (this.ticker && this.scrolledOnce) this.centerSpread();
    if (this.chartView === 'chart') this.scheduleChart();
  }

  private setChartView(v: 'ladder' | 'chart'): void {
    if (this.chartView === v) return;
    this.chartView = v;
    this.applyChartView();
  }

  private setChartType(t: ChartType): void {
    if (this.chartType === t) return;
    this.chartType = t;
    this.renderChartBar();
    this.renderRanges();
    void this.loadHistory();
    this.scheduleChart();
  }

  /** Time windows, or bar counts when the clock does not apply. */
  private renderRanges(): void {
    const ranges = this.q<HTMLElement>('.js-ranges');
    ranges.innerHTML = this.chartType === 'tick'
      ? TICK_LOOKBACKS.map((n) =>
        `<button class="rg-btn${n === this.tickBack ? ' on' : ''}" data-rg="${n}" title="Show the last ${n} bars">${n}</button>`).join('')
      : CHART_RANGES.map((r) =>
        `<button class="rg-btn${r.id === this.range.id ? ' on' : ''}" data-rg="${r.id}">${r.label}</button>`).join('');
  }

  /** Type buttons, plus the bar-size chip that only tick mode uses. */
  private renderChartBar(): void {
    const types = this.q<HTMLElement>('.js-ctypes');
    const btns = CHART_TYPES.map((c) =>
      `<button class="rg-btn${c.id === this.chartType ? ' on' : ''}" data-ct="${c.id}" title="${c.title}">${c.label}</button>`).join('');
    const n = this.chartType === 'tick'
      ? `<button class="rg-btn tick-n" data-tn="1" title="Trades per bar — click to cycle">${this.tickN}</button>`
      : '';
    types.innerHTML = btns + n;
  }

  /** A public print landed; the snapshot is coalesced and carries no trade size, so this builds from prints. */
  onTrade(tr: Trade): void {
    if (!this.ticker) return;
    this.prints.push(tr);
    if (this.prints.length > 40_000) this.prints.splice(0, this.prints.length - 40_000);
    if (this.vprofTicker === this.ticker) {
      const v = (this.vprof.get(tr.priceMc) ?? 0) + tr.mq;
      this.vprof.set(tr.priceMc, v);
      if (v > this.vprofMax) this.vprofMax = v;
      this.paintVolProfile();
    }
    if (this.chartView === 'chart' && this.chartType !== 'line') this.scheduleChart();
  }

  /** Volume traded at each price; paged on demand rather than at load. */
  private async loadVolProfile(): Promise<void> {
    const tk = this.ticker;
    if (!tk || this.vprofLoading || this.vprofTicker === tk) return;
    this.vprofLoading = true;
    try {
      const { byPrice, trades, complete } = await this.host.engine.volumeProfile(tk);
      if (this.ticker !== tk) return;
      this.vprof = byPrice;
      this.vprofMax = Math.max(0, ...byPrice.values());
      this.vprofTicker = tk;
      // Say so rather than let a partial profile pass as the whole history.
      const note = this.q<HTMLElement>('.js-chart-note');
      if (!complete && note) note.textContent = `volume profile: last ${trades} prints only`;
      this.paintVolProfile();
    } catch (e) {
      if (this.ticker === tk) toast(`Volume profile unavailable — ${(e as Error).message}`);
    } finally {
      this.vprofLoading = false;
    }
  }

  /** Size each row's bar against the busiest price level. */
  private paintVolProfile(): void {
    if (!this.colVisible.vol) return;
    const ladder = this.q<HTMLElement>('.js-ladder');
    const max = this.vprofMax || 1;
    // Look the column up by id, not a fixed index — column order can change.
    const at = this.colIndex().vol;
    if (at === undefined) return;
    for (const div of Array.from(ladder.children) as HTMLElement[]) {
      const price = Number(div.dataset.price);
      if (Number.isNaN(price)) continue;
      const cell = div.children[at] as HTMLElement | undefined;
      if (!cell) continue;
      const v = this.vprof.get(price) ?? 0;
      const bar = cell.firstElementChild as HTMLElement | null;
      if (bar) bar.style.width = v ? `${Math.max(2, (v / max) * 100)}%` : '0';
      // The point of control — where the most contracts changed hands.
      cell.classList.toggle('at-poc', v > 0 && v === this.vprofMax);
    }
  }

  /** Minute bars: backfilled candles, then live prints, which win for any minute they touch. */
  private minuteBars(): Bar[] {
    const back = this.bars.get(this.range.interval);
    return buildMinuteBars(
      back?.rows ?? [], this.prints, back?.takenAt ?? 0,
      this.range.interval * 60_000, Date.now(),
    );
  }


  private applyChartView(): void {
    const on = this.chartView === 'chart';
    this.el.classList.toggle('chart-on', on);
    for (const b of this.el.querySelectorAll<HTMLElement>('.js-view')) {
      b.classList.toggle('on', b.dataset.view === this.chartView);
    }
    if (on) {
      void this.loadHistory();
      this.scheduleChart();
    }
  }

  /** Points to draw: finest backfill available, then the live tail; hourly candles only fill what minute data never reached. */
  private chartPoints(): HistPoint[] {
    let out = this.hist.get(60)?.pts.slice() ?? [];
    const fine = this.hist.get(1)?.pts;
    if (fine?.length) {
      const from = fine[0]!.t;
      out = out.filter((p) => p.t < from).concat(fine);
    }
    const lastT = out.length ? out[out.length - 1]!.t : 0;
    for (const p of this.live) if (p.t > lastT) out.push(p);
    return out;
  }

  private resetHistory(): void {
    this.hist.clear();
    this.live = [];
    this.bars.clear();
    this.prints = [];
    this.tradeCount = 0;
    this.seriesTicker = '';
  }

  /** Bars for whichever bar mode is selected. */
  private chartBars(): Bar[] {
    if (this.chartType !== 'tick') return this.minuteBars();
    const bars = buildTickBars(this.prints, this.tickN);
    return bars.slice(Math.max(0, bars.length - this.tickBack));
  }

  /** Backfill for the bar modes: minute bars come from candles, tick bars page the trade feed. */
  private async loadBars(tk: string, note: HTMLElement): Promise<void> {
    const want = this.range;
    if (this.chartType === 'min') {
      const have = this.bars.get(want.interval);
      if (have && have.ms >= want.ms) return;
      note.textContent = 'loading bars…';
      const takenAt = Date.now();
      const rows = await this.host.engine.priceBars(tk, Math.ceil(want.ms / 60_000), want.interval);
      if (this.ticker !== tk) return;
      // `takenAt` rides with the bars, not a shared field, to avoid tick-mode backfill clobbering it.
      this.bars.set(want.interval, { rows, ms: want.ms, takenAt });
      note.textContent = rows.length ? '' : 'no trades in this window';
      return;
    }
    // Tick mode: fetch enough prints to fill the requested number of bars.
    const wantTrades = this.tickBack * this.tickN;
    if (this.tradeCount >= wantTrades) return;
    note.textContent = 'loading trades…';
    const { trades, hitCap } = await this.host.engine.tradeHistory(tk, wantTrades);
    if (this.ticker !== tk) return;
    // Live prints kept arriving while paging; splice the backfill in front rather than dropping either side.
    const newest = trades[trades.length - 1]?.t ?? 0;
    const liveTail = this.prints.filter((p) => p.t > newest);
    this.prints = trades.concat(liveTail);
    this.tradeCount = wantTrades;
    const short = trades.length < wantTrades;
    note.textContent = trades.length
      ? (short
        ? `${Math.floor(trades.length / this.tickN)} bars — ${hitCap ? 'page cap reached' : 'that is all the market has printed'}`
        : '')
      : 'no trades yet';
  }

  /** Backfills history for the selected range; refetches only when the window grows or the ticker changes. */
  private async loadHistory(): Promise<void> {
    const tk = this.ticker;
    const note = this.q<HTMLElement>('.js-chart-note');
    if (!tk) {
      this.resetHistory();
      note.textContent = '';
      return;
    }
    if (this.seriesTicker !== tk) this.resetHistory();

    if (this.chartType !== 'line') {
      this.seriesTicker = tk;
      try {
        await this.loadBars(tk, note);
      } catch (e) {
        if (this.ticker !== tk) return;
        note.textContent = `history unavailable — streaming live (${(e as Error).message})`;
      }
      this.scheduleChart();
      return;
    }

    const want = this.range;
    const have = this.hist.get(want.interval);
    if (this.seriesTicker === tk && have && have.ms >= want.ms) return;

    this.seriesTicker = tk;
    note.textContent = 'loading history…';
    try {
      const minutes = Math.ceil(want.ms / 60_000);
      const rows = await this.host.engine.bidAskHistory(tk, minutes, want.interval);
      if (this.ticker !== tk) return; // switched away mid-flight
      this.hist.set(want.interval, { pts: rows, ms: want.ms });
      note.textContent = this.chartPoints().length
        ? ''
        : 'no history for this window — streaming live';
      // Candles land on the period boundary, so anchor the right edge to the live top of book.
      if (this.lastBestBid !== null || this.lastBestAsk !== null) {
        this.pushSample(this.lastBestBid, this.lastBestAsk);
      }
    } catch (e) {
      if (this.ticker !== tk) return;
      note.textContent = `history unavailable — streaming live (${(e as Error).message})`;
    }
    this.scheduleChart();
  }

  /** Append the current top of book. Called from every snapshot. */
  private pushSample(bid: number | null, ask: number | null): void {
    const last = this.live[this.live.length - 1];
    const t = Date.now();
    // Skip identical consecutive points, or a quiet book fills the buffer and squashes the time window.
    if (last && last.bid === bid && last.ask === ask && t - last.t < 30_000) return;
    this.live.push({ t, bid, ask });
    if (this.live.length > 4000) this.live.splice(0, this.live.length - 4000);
    if (this.chartView === 'chart') this.scheduleChart();
  }

  private scheduleChart(): void {
    if (this.chartRaf) return;
    this.chartRaf = requestAnimationFrame(() => {
      this.chartRaf = 0;
      this.drawChart();
    });
  }

  /** OHLC bars sharing the ladder's price axis; tick bars use non-linear, per-bar-close time labels. */
  private drawBars(
    ctx: CanvasRenderingContext2D, w: number, h: number,
    yOf: (mc: number) => number, css: CSSStyleDeclaration, note: HTMLElement,
  ): void {
    const AXIS_H = 15;
    const dim = css.getPropertyValue('--dim2').trim() || '#939293';
    const grid = css.getPropertyValue('--border').trim() || '#4a464b';
    const upCol = css.getPropertyValue('--bid-txt').trim() || '#78dce8';
    const dnCol = css.getPropertyValue('--ask-txt').trim() || '#ff6188';

    const all = this.chartBars();
    const byTime = this.chartType === 'min';
    const t1 = Date.now();
    const t0 = t1 - this.range.ms;
    const bars = byTime ? all.filter((b) => b.t1 > t0) : all;

    if (!bars.length) {
      if (!note.textContent) note.textContent = 'no trades yet in this window';
      return;
    }
    if (note.textContent === 'no trades yet in this window') note.textContent = '';

    // Bar geometry: real time for minute bars, fixed slots for tick bars.
    let slotW: number;
    let xMid: (i: number, b: Bar) => number;
    let shown = bars;
    if (byTime) {
      const span = Math.max(1, t1 - t0);
      slotW = Math.max(1, (this.range.interval * 60_000 / span) * w);
      // An unfinished bar's midpoint is in the future; anchor it to now instead of clipping past the edge.
      xMid = (_i, b) => w - ((t1 - Math.min((b.t0 + b.t1) / 2, t1)) / span) * w;
    } else {
      // Show the newest bars that still fit at a legible width.
      const target = 7;
      const fit = Math.max(1, Math.floor(w / target));
      shown = bars.slice(Math.max(0, bars.length - fit));
      slotW = w / shown.length;
      xMid = (i) => (i + 0.5) * slotW;
    }
    const bodyW = Math.max(1, Math.min(14, slotW * 0.7));

    ctx.font = '9px ui-monospace, Menlo, monospace';
    ctx.textBaseline = 'bottom';
    ctx.lineWidth = 1;
    const span = Math.max(1, t1 - t0);
    const marks: { x: number; label: string }[] = [];
    if (byTime) {
      for (let i = 0; i <= 4; i++) {
        const t = t0 + (span * i) / 4;
        marks.push({ x: w - ((t1 - t) / span) * w, label: fmtClock(t, span) });
      }
    } else {
      const step = Math.max(1, Math.ceil(shown.length / 4));
      for (let i = 0; i < shown.length; i += step) {
        const b = shown[i]!;
        marks.push({ x: xMid(i, b), label: fmtClock(b.t1, shown[shown.length - 1]!.t1 - shown[0]!.t0) });
      }
    }
    for (const m of marks) {
      const x = Math.round(m.x) + 0.5;
      ctx.globalAlpha = 0.45;
      ctx.strokeStyle = grid;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h - AXIS_H);
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.fillStyle = dim;
      const tw = ctx.measureText(m.label).width;
      ctx.fillText(m.label, Math.min(Math.max(x - tw / 2, 2), w - tw - 2), h - 3);
    }
    ctx.globalAlpha = 0.45;
    ctx.strokeStyle = grid;
    ctx.beginPath();
    ctx.moveTo(0, h - AXIS_H + 0.5);
    ctx.lineTo(w, h - AXIS_H + 0.5);
    ctx.stroke();
    ctx.globalAlpha = 1;

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, w, h - AXIS_H);
    ctx.clip();
    shown.forEach((b, i) => {
      const up = b.c >= b.o;
      const col = up ? upCol : dnCol;
      // Keep the newest bar's body fully on the canvas rather than half-cut.
      const x = Math.min(xMid(i, b), w - bodyW / 2 - 1);
      const xr = Math.round(x) + 0.5;
      ctx.strokeStyle = col;
      ctx.fillStyle = col;
      // The forming bar is drawn hollow-ish so it reads differently from a finalized one.
      ctx.globalAlpha = b.forming ? 0.55 : 1;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(xr, yOf(b.h));
      ctx.lineTo(xr, yOf(b.l));
      ctx.stroke();
      const yo = yOf(b.o);
      const yc = yOf(b.c);
      const top = Math.min(yo, yc);
      const hgt = Math.max(1, Math.abs(yc - yo));
      ctx.fillRect(x - bodyW / 2, top, bodyW, hgt);
      ctx.globalAlpha = 1;
    });
    ctx.restore();
  }

  private drawChart(): void {
    const cv = this.q<HTMLCanvasElement>('.js-chart-cv');
    const box = this.q<HTMLElement>('.js-chart');
    const ladder = this.q<HTMLElement>('.js-ladder');
    const note = this.q<HTMLElement>('.js-chart-note');
    const w = box.clientWidth;
    const h = box.clientHeight;
    if (!w || !h || !cv.getContext) return;

    const dpr = window.devicePixelRatio || 1;
    if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) {
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
      cv.style.width = `${w}px`;
      cv.style.height = `${h}px`;
    }
    const ctx = cv.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // Clearing and bailing matters: the canvas keeps its last frame otherwise, showing a stale market's lines.
    const rows = ladder.children as HTMLCollectionOf<HTMLElement>;
    if (!this.ticker || rows.length < 2 || !rows[0]?.dataset.price) {
      note.textContent = this.ticker ? 'waiting for the book…' : 'no contract loaded';
      return;
    }

    const css = getComputedStyle(this.el);
    const dim = css.getPropertyValue('--dim2').trim() || '#939293';
    const grid = css.getPropertyValue('--border').trim() || '#4a464b';

    // Price -> y, taken from the ladder itself.
    const topMc = Number(rows[0].dataset.price);
    const rowH = rows[1].offsetTop - rows[0].offsetTop;
    const tick = this.tickMc || 1;
    if (!rowH) return;
    // Row 0 is not at y=0 on the canvas: the ladder body starts below its column header.
    const yShift = ladder.getBoundingClientRect().top - box.getBoundingClientRect().top;
    const yOf = (mc: number) => ((topMc - mc) / tick) * rowH + rowH / 2 - ladder.scrollTop + yShift;

    if (this.chartType !== 'line') {
      this.drawBars(ctx, w, h, yOf, css, note);
      return;
    }

    const pts = this.chartPoints();
    const t1 = Date.now();
    const t0 = this.range.id === 'all' && pts.length
      ? Math.min(pts[0].t, t1 - 60_000)
      : t1 - this.range.ms;
    const xOf = (t: number) => w - ((t1 - t) / (t1 - t0)) * w;

    const AXIS_H = 15;
    const span = t1 - t0;
    const ticks = 4;
    ctx.font = '9px ui-monospace, Menlo, monospace';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = dim;
    ctx.strokeStyle = grid;
    ctx.lineWidth = 1;
    for (let i = 0; i <= ticks; i++) {
      const t = t0 + (span * i) / ticks;
      const x = Math.round(xOf(t)) + 0.5;
      ctx.globalAlpha = 0.45;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h - AXIS_H);
      ctx.stroke();
      ctx.globalAlpha = 1;
      const label = fmtClock(t, span);
      const tw = ctx.measureText(label).width;
      // Keep the newest label inside the canvas rather than clipped at the edge.
      const lx = Math.min(Math.max(x - tw / 2, 2), w - tw - 2);
      ctx.fillText(label, lx, h - 3);
    }
    ctx.globalAlpha = 0.45;
    ctx.beginPath();
    ctx.moveTo(0, h - AXIS_H + 0.5);
    ctx.lineTo(w, h - AXIS_H + 0.5);
    ctx.stroke();
    ctx.globalAlpha = 1;

    if (pts.length < 2) {
      note.textContent = 'streaming — not enough history yet';
      return;
    }
    if (note.textContent === 'streaming — not enough history yet') note.textContent = '';

    // Quotes are step functions: a price holds until it changes, then jumps — not a diagonal line.
    const step = (key: 'bid' | 'ask', colour: string) => {
      ctx.beginPath();
      let prev: { x: number; y: number } | null = null;
      for (const p of pts) {
        const v = p[key];
        if (v === null || v === undefined) { prev = null; continue; }
        const x = xOf(p.t);
        const y = yOf(v);
        if (prev) {
          ctx.lineTo(x, prev.y); // hold the old price until this instant…
          ctx.lineTo(x, y);      // …then jump
        } else {
          ctx.moveTo(x, y);
        }
        prev = { x, y };
      }
      // Carry the last known quote to "now" so the line reaches the book edge.
      if (prev) ctx.lineTo(w, prev.y);
      ctx.strokeStyle = colour;
      ctx.lineWidth = 1.5;
      ctx.lineJoin = 'miter';
      ctx.stroke();
    };

    // Draw each backfilled candle's high-low range faintly so a swing doesn't read as a flat hold.
    const wicks = (hiK: 'bidHi' | 'askHi', loK: 'bidLo' | 'askLo', colour: string) => {
      ctx.strokeStyle = colour;
      ctx.globalAlpha = 0.35;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (const p of pts) {
        const hi = p[hiK];
        const lo = p[loK];
        if (hi == null || lo == null || hi === lo) continue;
        const x = Math.round(xOf(p.t)) + 0.5;
        ctx.moveTo(x, yOf(hi));
        ctx.lineTo(x, yOf(lo));
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    };

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, w, h - AXIS_H);
    ctx.clip();
    const bidCol = css.getPropertyValue('--bid-txt').trim() || '#78dce8';
    const askCol = css.getPropertyValue('--ask-txt').trim() || '#ff6188';
    wicks('bidHi', 'bidLo', bidCol);
    wicks('askHi', 'askLo', askCol);
    step('bid', bidCol);
    step('ask', askCol);
    ctx.restore();
  }

  /** Scroll so the spread sits vertically centred; uses getBoundingClientRect so header height and scrollbars don't matter. */
  private centerSpread(): void {
    const ladder = this.q<HTMLElement>('.js-ladder');
    const b = ladder.querySelector('.best-bid');
    const a = ladder.querySelector('.best-ask');
    const ref = b || a;
    if (!ref) return;
    const lr = ladder.getBoundingClientRect();
    const centerOf = (el: Element) => {
      const r = el.getBoundingClientRect();
      return r.top + r.height / 2;
    };
    const mid = b && a ? (centerOf(b) + centerOf(a)) / 2 : centerOf(ref);
    ladder.scrollTop += (mid - lr.top) - ladder.clientHeight / 2;
  }

  /** Colour the price cell where THIS panel's selected account filled today.
   *  Blue = bid (yes) fill, red = ask (no) fill, purple = two or more. */
  /** Text writes that skip the DOM when nothing changed; reading textContent forces no layout. */
  private setText(sel: string, text: string): void {
    const el = this.q(sel);
    if (el.textContent !== text) el.textContent = text;
  }

  /** Like setText for markup built by unitHtml; skips the write when nothing changed. */
  private setHtml(sel: string, html: string): void {
    const el = this.q(sel) as HTMLElement;
    if (el.dataset.html !== html) {
      el.innerHTML = html;
      el.dataset.html = html;
    }
  }

  private applyFillColors(): void {
    // A venue with no accounts files its fills under '' — they are everyone's.
    const fills = this.fillsByAccount[this.accountId] ?? this.fillsByAccount[''] ?? {};
    for (const v of this.ensureRowViews()) {
      if (!v.fillCell) continue;
      const f = fills[v.price];
      const b = f ? f[0] : 0;
      const a = f ? f[1] : 0;
      const kind = b + a >= 2 ? 3 : b > 0 ? 1 : a > 0 ? 2 : 0;
      if (kind === v.fill$) continue;
      [v.fillCell.style.background, v.fillCell.style.color] = FILL_STYLE[kind];
      v.fill$ = kind;
    }
  }

  private applyBracketOutlines(): void {
    const b = this.bracketsByAccount[this.accountId] ?? { entry: [], hedge: [] };
    const levels = new Set([...b.entry, ...b.hedge]);
    for (const v of this.ensureRowViews()) {
      if (!v.bracketCell) continue;
      const on = levels.has(v.price);
      if (on === v.bracket$) continue;
      v.bracketCell.style.boxShadow = on ? BRACKET_OUTLINE : '';
      v.bracket$ = on;
    }
  }

  /** Row views for the current ladder DOM; re-resolved only after the rows themselves were rebuilt. */
  private ensureRowViews(): RowView[] {
    const ladder = this.q<HTMLElement>('.js-ladder');
    const cur = this.rowViews;
    if (cur.length && cur[0].div.parentElement === ladder && cur[cur.length - 1].div === ladder.lastElementChild
      && cur[0].div === ladder.firstElementChild) return cur;
    const idx = this.colIndex();
    const views: RowView[] = [];
    for (const div of Array.from(ladder.children) as HTMLElement[]) {
      const price = Number(div.dataset.price);
      if (Number.isNaN(price)) continue;
      const kids = div.children as HTMLCollectionOf<HTMLElement>;
      const at = (id: ColId) => (idx[id] === undefined ? null : kids[idx[id]!] ?? null);
      const depth = at('depth');
      // Starting values match a freshly built row, so nothing is rewritten just because it is new.
      views.push({
        price, div,
        my: at('my'), bid: at('bid'), ask: at('ask'), queue: at('queue'),
        depth, bar: depth?.firstElementChild as HTMLElement ?? null, mark: depth?.lastElementChild as HTMLElement ?? null,
        fillCell: at('price'), bracketCell: at('my') ?? at('price'),
        my$: '', myCls$: 'c-myq', bid$: '', ask$: '', queue$: '', depthSide$: '', barW$: '0', markLeft$: '',
        best$: '', fill$: 0, bracket$: false,
      });
    }
    // Rows that were not freshly built may carry state this cache never saw; start from the DOM.
    for (const v of views) this.syncFromDom(v);
    this.rowViews = views;
    return views;
  }

  /** Read the row's current state so the cache matches the DOM even when it was not built empty. */
  private syncFromDom(v: RowView): void {
    if (v.my) { v.my$ = v.my.textContent ?? ''; v.myCls$ = v.my.className; }
    if (v.bid) v.bid$ = v.bid.textContent ?? '';
    if (v.ask) v.ask$ = v.ask.textContent ?? '';
    if (v.queue) v.queue$ = v.queue.textContent ?? '';
    if (v.depth) v.depthSide$ = v.depth.classList.contains('side-bid') ? 'bid' : v.depth.classList.contains('side-ask') ? 'ask' : '';
    if (v.bar) v.barW$ = v.bar.style.width || '0';
    // A marker never written yet reads as unknown, so the first paint sets it explicitly as before.
    if (v.mark) {
      v.markLeft$ = v.mark.style.display === 'none' ? '' : v.mark.style.display === 'block' && v.mark.style.left ? v.mark.style.left : MARK_UNSET;
    }
    const b = v.div.classList.contains('best-bid');
    const a = v.div.classList.contains('best-ask');
    v.best$ = b && a ? 'both' : b ? 'bid' : a ? 'ask' : '';
    if (v.fillCell) v.fill$ = Math.max(0, FILL_STYLE.findIndex(([bg]) => bg === v.fillCell!.style.background));
    if (v.bracketCell) v.bracket$ = !!v.bracketCell.style.boxShadow;
  }

  /** Returns the placed order id, or null on any failure (already toasted). */
  private async placeOrder(priceMc: number, side: 'yes' | 'no', mode: 'post' | 'fok'): Promise<{ order_id: string; status: string } | null> {
    if (!this.ticker || !this.tradingEnabled) return null;
    if (!this.accountId) {
      toast('Select a trading account first (Account button).');
      return null;
    }
    if (!this.selectedSizeMq) {
      toast('size is 0');
      return null;
    }
    if (this.host.isStale()) {
      toast('DISCONNECTED — data stale, order not sent', 'error');
      return null;
    }
    // Per-panel in-flight guard: one placement at a time, so a double-click can't become two orders.
    if (this.placing) return null;
    this.placing = true;
    try {
      const qtyMq = this.selectedSizeMq;
      const res = await this.host.engine.placeOrder({
        ticker: this.ticker, side, priceMc, qtyMq, mode, accountId: this.accountId,
      });
      if (!res.ok) {
        const lc = res.error.toLowerCase();
        // Only call it a balance problem when it is one; other insufficient_* codes exist too.
        const isBalance = /insufficient[_ ]balance/.test(lc);
        toast(isBalance ? 'Insufficient balance for that order' : 'Order rejected: ' + res.error, 'error');
        return null;
      }
      if (!res.order.order_id) {
        toast('Order response missing order id — check Orders Out', 'error');
        return null;
      }
      toast(`${formatMq(qtyMq)} @ ${formatMc(priceMc)}¢ ${side}`, 'ok');
      return { order_id: res.order.order_id, status: res.order.status };
    } finally {
      this.placing = false;
    }
  }

  /** Click 1 places the passive entry and enters phantom mode; click 2 on the opposite side arms the hedge. */
  private async bracketClick(isBid: boolean, isAsk: boolean, price: number): Promise<void> {
    if (!this.accountId) {
      toast('Select a trading account first (Account button).');
      return;
    }
    if (!this.bracketPending) {
      const side: 'yes' | 'no' = isBid ? 'yes' : 'no';
      const entryPrice = isBid ? price : MC_SETTLE - price;
      const qtyMq = this.selectedSizeMq;
      // Let Kalshi's post_only decide whether it would cross, rather than a client-side best-ask guess.
      const order = await this.placeOrder(entryPrice, side, 'post');
      if (!order) return; // placeOrder already explained why
      // Only arm phantom mode if the entry actually rested.
      if (order.status && !['resting', 'open', 'partially_filled', 'pending'].includes(order.status)) {
        toast(`Bracket entry not resting (status: ${order.status}) — bracket not armed.`, 'error');
        return;
      }
      this.bracketPending = {
        entryOrderId: order.order_id,
        hedgeCell: isBid ? 'ask' : 'bid',
        entrySide: side,
        entryQtyMq: qtyMq,
        seen: false,
      };
      this.setPhantom(true, this.bracketPending.hedgeCell);
      // If the entry is never seen active within 15s, bail rather than intercept clicks forever.
      clearTimeout(this.phantomTimer);
      this.phantomTimer = window.setTimeout(() => {
        if (this.bracketPending && !this.bracketPending.seen) {
          this.cancelPhantom();
          toast('bracket entry never rested — phantom mode cancelled', 'error');
        }
      }, 15_000);
      toast('Entry placed — click the opposite side to set the hedge.', 'ok');
      return;
    }

    const want = this.bracketPending.hedgeCell;
    if ((want === 'ask' && !isAsk) || (want === 'bid' && !isBid)) {
      toast('Click the opposite side to set the hedge level.');
      return;
    }
    const hedgeSide: 'yes' | 'no' = isBid ? 'yes' : 'no';
    const hedgePriceMc = isBid ? price : MC_SETTLE - price;
    const r = await this.host.engine.armBracket({
      ticker: this.ticker!,
      entryOrderId: this.bracketPending.entryOrderId,
      hedgeSide,
      hedgePriceMc,
      accountId: this.accountId,
      entrySide: this.bracketPending.entrySide,
      entryQtyMq: this.bracketPending.entryQtyMq,
    });
    toast(r.ok ? 'Bracket armed.' : 'Bracket failed: ' + r.error, r.ok ? 'ok' : 'error');
    this.cancelPhantom();
  }

  private setPhantom(on: boolean, hedgeCell?: 'bid' | 'ask'): void {
    const pr = this.q<HTMLElement>('.p-right');
    pr.classList.toggle('phantom', on);
    pr.classList.remove('phantom-bid', 'phantom-ask');
    if (on && hedgeCell) pr.classList.add(hedgeCell === 'ask' ? 'phantom-ask' : 'phantom-bid');
  }

  private cancelPhantom(): void {
    clearTimeout(this.phantomTimer);
    this.phantomTimer = undefined;
    this.bracketPending = null;
    this.setPhantom(false);
  }

  private showLevelOrders(cell: HTMLElement, rowPrice: number): void {
    const orders = this.orders.filter((o) =>
      ACTIVE.includes(o.status)
      && o.price_mc !== null
      && (o.side === 'yes' ? o.price_mc : MC_SETTLE - o.price_mc) === rowPrice);
    document.getElementById('kg-order-pop')?.remove();
    if (!orders.length) return;

    const pop = document.createElement('div');
    pop.id = 'kg-order-pop';
    pop.className = 'kg-menu';
    const r = cell.getBoundingClientRect();
    pop.style.top = `${r.bottom + 2}px`;
    pop.style.left = `${Math.min(r.left, window.innerWidth - 210)}px`;
    pop.style.width = '200px';
    const accts = this.host.accounts();
    pop.innerHTML =
      `<div style="padding:4px 7px;border-bottom:1px solid var(--border);color:var(--dim2);font-size:9px;text-transform:uppercase">Orders @ ${formatMc(rowPrice)}¢ · ✕ or right-click to cancel</div>`
      + orders.map((o) => {
        const buy = o.side === 'yes';
        const acct = accts.find((a) => a.id === o.account_id)?.name ?? o.account_id ?? '';
        return `<div class="kg-ord-row" data-oid="${escHtml(o.order_id)}" style="display:flex;justify-content:space-between;align-items:center;gap:6px;padding:4px 7px;border-bottom:1px solid var(--hairline)">`
          + `<span style="color:${buy ? 'var(--bid-txt)' : 'var(--ask-txt)'};font-weight:600;flex-shrink:0">${buy ? 'BUY' : 'SELL'} ${formatMq(o.remaining_mq)}</span>`
          + `<span style="opacity:.6;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml(acct)}</span>`
          + '<span class="kg-ord-x" title="cancel" style="color:var(--bad);cursor:pointer;flex-shrink:0;font-weight:700">✕</span></div>';
      }).join('');
    document.body.appendChild(pop);

    const cancel = async (oid: string) => {
      pop.remove();
      if (!this.tradingEnabled) return;
      const res = await this.host.engine.cancelOrder(oid);
      if (!res.ok) toast('CANCEL FAILED — order may still be resting: ' + res.error, 'error');
    };
    pop.querySelectorAll<HTMLElement>('.kg-ord-row').forEach((rw) => {
      rw.querySelector('.kg-ord-x')!.addEventListener('click', (e) => {
        e.stopPropagation();
        void cancel(rw.dataset.oid!);
      });
      rw.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        void cancel(rw.dataset.oid!);
      });
    });
    const close = (e: MouseEvent) => {
      if (!pop.contains(e.target as Node)) {
        pop.remove();
        document.removeEventListener('mousedown', close, true);
      }
    };
    setTimeout(() => document.addEventListener('mousedown', close, true), 0);
  }

  private toggleFills(): void {
    const t = this.q<HTMLElement>('.js-fills-tracker');
    const open = t.style.display === 'none' || !t.style.display;
    t.style.display = open ? 'flex' : 'none';
    // No polling: backfill once on open, then live fills arrive via the engine.
    if (open) void this.loadFills();
  }

  private async loadFills(): Promise<void> {
    if (!this.ticker) {
      this.fillsData = [];
      this.renderFills();
      return;
    }
    this.fillsData = await this.host.engine.recentFills(this.ticker, this.accountId || undefined, 100);
    this.renderFills();
  }

  onFillEvent(ev: { ticker: string; account_id: string; ts: number; side: 'yes' | 'no'; mq: number; yes_price_mc: number; is_taker: boolean }): void {
    if (ev.ticker !== this.ticker) return;
    if (this.accountId && ev.account_id !== this.accountId) return;
    this.fillsData.unshift({
      fill_id: '', ts: ev.ts, ticker: ev.ticker, side: ev.side, mq: ev.mq,
      yes_price_mc: ev.yes_price_mc, fee_cash: 0, is_taker: ev.is_taker,
      order_id: '', client_order_id: '', account_id: ev.account_id,
    });
    this.fillsData = this.fillsData.slice(0, 100);
    const t = this.q<HTMLElement>('.js-fills-tracker');
    if (t.style.display === 'flex') this.renderFills();
  }

  private renderFills(): void {
    const list = this.q<HTMLElement>('.js-fills-list');
    if (!this.fillsData.length) {
      list.innerHTML = '<div style="padding:8px 6px;color:var(--dim2);font-size:10px">No fills yet</div>';
      return;
    }
    list.innerHTML = this.fillsData.map((f) => {
      const buy = f.side === 'yes'; // yes = bought YES (blue), no = sold YES (red)
      const col = buy ? 'var(--bid-txt)' : 'var(--ask-txt)';
      const t = new Date((f.ts || 0) * 1000).toLocaleTimeString('en-US', { hour12: false });
      const liq = f.is_taker ? 'rem' : 'add'; // taker removed liquidity / maker added
      return `<div class="fill-row" style="color:${col}">`
        + `<span style="flex:0 0 58px;color:var(--dim1)">${t}</span>`
        + `<span style="flex:0 0 40px;font-weight:700">${buy ? 'BUY' : 'SELL'}</span>`
        + `<span style="flex:0 0 46px;text-align:right">${formatMc(f.yes_price_mc)}¢</span>`
        + `<span style="flex:0 0 40px;text-align:right">${formatMq(f.mq)}</span>`
        + `<span style="flex:1;text-align:right;color:var(--dim2)">${liq}</span></div>`;
    }).join('');
  }

  setAccount(id: string): void {
    this.accountId = id;
    if (id) localStorage.setItem('kg_acct_' + this.id, id);
    else localStorage.removeItem('kg_acct_' + this.id);
    this.refreshAcct();
  }

  refreshAcct(): void {
    const btn = this.q<HTMLElement>('.js-acct-btn');
    const accounts = this.host.accounts();
    // No auto-default: a stored selection is honoured, otherwise the user picks.
    if (this.accountId && !accounts.some((a) => a.id === this.accountId)) {
      this.accountId = '';
      localStorage.removeItem('kg_acct_' + this.id);
    }
    const cur = accounts.find((a) => a.id === this.accountId);
    btn.textContent = cur ? cur.name : 'Select account';
    btn.style.color = cur ? '' : 'var(--accent)';
    btn.title = cur ? `Trading into ${cur.name} (${cur.id})` : 'Select trading account';
    this.applyFillColors();
    this.applyBracketOutlines();
    this.applyAccountStats();
    const ft = this.q<HTMLElement>('.js-fills-tracker');
    if (ft.style.display === 'flex') void this.loadFills();
  }

  private openAcctMenu(): void {
    document.getElementById('kg-acct-menu')?.remove();
    const btn = this.q<HTMLElement>('.js-acct-btn');
    const menu = document.createElement('div');
    menu.id = 'kg-acct-menu';
    menu.className = 'kg-menu';
    const r = btn.getBoundingClientRect();
    menu.style.top = `${r.bottom + 2}px`;
    menu.style.left = `${Math.min(r.left, window.innerWidth - 220)}px`;
    menu.style.width = '210px';
    menu.style.maxHeight = '260px';
    menu.innerHTML = '<input class="js-acct-search" placeholder="search or type a new name…" autocomplete="off"/><div class="js-acct-list"></div>';
    document.body.appendChild(menu);

    const search = menu.querySelector<HTMLInputElement>('.js-acct-search')!;
    const list = menu.querySelector<HTMLElement>('.js-acct-list')!;

    const render = (filter: string) => {
      const raw = filter.trim();
      const f = raw.toLowerCase();
      const items = this.host.accounts()
        .filter((a) => a.name.toLowerCase().includes(f) || a.id.toLowerCase().includes(f));
      let html = items.map((a) =>
        `<div class="acct-item${a.id === this.accountId ? ' selected' : ''}" data-id="${a.id}">`
        + `<span>${escHtml(a.name)}</span><span class="aid">${a.id}</span></div>`).join('');
      const exists = this.host.accounts().some((a) => a.name.toLowerCase() === f);
      if (raw && !exists) {
        html += `<div class="acct-item js-acct-new" data-name="${escHtml(raw)}" style="border-top:1px solid var(--border);color:var(--accent)">+ Create “${escHtml(raw)}”</div>`;
      }
      if (!html) html = '<div style="padding:5px 7px;opacity:.5">type a name to create an account…</div>';
      list.innerHTML = html;
    };
    render('');
    search.focus();
    search.addEventListener('input', () => render(search.value));

    list.addEventListener('click', async (ev) => {
      const target = ev.target as HTMLElement;
      const newBtn = target.closest<HTMLElement>('.js-acct-new');
      if (newBtn?.dataset.name) {
        const res = await this.host.engine.createAccount(newBtn.dataset.name);
        if (!res.ok) {
          list.insertAdjacentHTML('afterbegin', `<div style="padding:5px 7px;color:var(--bad)">${escHtml(res.error)}</div>`);
          return;
        }
        this.setAccount(res.account.id);
        menu.remove();
        return;
      }
      const item = target.closest<HTMLElement>('.acct-item');
      if (!item?.dataset.id) return;
      if (this.ticker && this.contractTaken(this.ticker, item.dataset.id)) {
        toast(`${this.ticker} is already open on that account in another panel.`);
        return;
      }
      this.setAccount(item.dataset.id);
      menu.remove();
    });

    const close = (e: MouseEvent) => {
      if (!menu.contains(e.target as Node)) {
        menu.remove();
        document.removeEventListener('mousedown', close, true);
      }
    };
    setTimeout(() => document.addEventListener('mousedown', close, true), 0);
  }

  /** The columns actually on screen, in order. */
  private visibleCols(): ColDef[] {
    return this.colOrder.map((id) => COL_BY_ID.get(id)!).filter((c) => c && this.colVisible[c.id]);
  }

  /** Where each visible column sits, for addressing a row's cells. */
  private colIndex(): Partial<Record<ColId, number>> {
    const out: Partial<Record<ColId, number>> = {};
    this.visibleCols().forEach((c, i) => { out[c.id] = i; });
    return out;
  }

  /** A signature that changes whenever the rows need rebuilding. */
  private colSig(): string {
    return this.visibleCols().map((c) => c.id).join(',');
  }

  private saveCols(): void {
    try {
      localStorage.setItem(COLS_KEY, JSON.stringify({
        order: this.colOrder, visible: this.colVisible, widths: this.colWidths,
      }));
    } catch { /* private mode; layout just will not persist */ }
  }

  private loadCols(): void {
    try {
      const raw = localStorage.getItem(COLS_KEY);
      if (!raw) return;
      const v = JSON.parse(raw);
      // Merge rather than replace, so a column added later still appears for an older saved layout.
      if (Array.isArray(v.order)) {
        const known = v.order.filter((id: ColId) => COL_BY_ID.has(id));
        this.colOrder = [...known, ...COLS.map((c) => c.id).filter((id) => !known.includes(id))];
      }
      for (const c of COLS) {
        if (typeof v.visible?.[c.id] === 'boolean') this.colVisible[c.id] = v.visible[c.id];
        if (typeof v.widths?.[c.id] === 'number') this.colWidths[c.id] = v.widths[c.id];
      }
      this.colVisible.price = true;   // never hideable, whatever was stored
    } catch { /* corrupt layout: fall back to defaults */ }
  }

  /** Header cells, and in edit mode the drag handles that reorder them. */
  private renderColHead(): void {
    const head = this.q<HTMLElement>('.ldr-head');
    head.querySelectorAll('.col-handle').forEach((h) => h.remove());
    head.replaceChildren(...this.visibleCols().map((c) => {
      const d = document.createElement('div');
      d.textContent = c.label;
      d.dataset.col = c.id;
      if (c.title) d.title = c.title;
      if (this.editMode) {
        d.draggable = true;
        d.classList.add('col-drag');
        d.addEventListener('dragstart', (ev) => {
          ev.dataTransfer?.setData('text/plain', c.id);
          d.classList.add('dragging');
        });
        d.addEventListener('dragend', () => d.classList.remove('dragging'));
        d.addEventListener('dragover', (ev) => { ev.preventDefault(); d.classList.add('col-over'); });
        d.addEventListener('dragleave', () => d.classList.remove('col-over'));
        d.addEventListener('drop', (ev) => {
          ev.preventDefault();
          d.classList.remove('col-over');
          const from = ev.dataTransfer?.getData('text/plain') as ColId | undefined;
          if (!from || from === c.id) return;
          this.moveColumn(from, c.id);
        });
      }
      return d;
    }));
    this.applyColWidths();
    this.syncHandles();
  }

  /** Drop `from` into `before`'s slot, keeping every other column's order. */
  private moveColumn(from: ColId, before: ColId): void {
    const order = this.colOrder.filter((id) => id !== from);
    const at = order.indexOf(before);
    order.splice(at < 0 ? order.length : at, 0, from);
    this.colOrder = order;
    this.saveCols();
    this.renderColHead();
    this.rebuildLadder();
    this.paintVolProfile();   // bars are imperative; fresh cells start empty
  }

  private renderColMenu(): void {
    const menu = this.q<HTMLElement>('.js-col-menu');
    menu.innerHTML = this.colOrder.map((id) => {
      const c = COL_BY_ID.get(id)!;
      const dis = c.fixed ? ' disabled' : '';
      return `<label class="cm-row${c.fixed ? ' cm-fixed' : ''}"${c.fixed ? ' title="The ladder needs its price column"' : ''}>`
        + `<input type="checkbox" class="js-col-check" data-col="${c.id}"`
        + `${this.colVisible[c.id] ? ' checked' : ''}${dis}/><span>${c.label}</span></label>`;
    }).join('');
    for (const cb of menu.querySelectorAll<HTMLInputElement>('.js-col-check')) {
      cb.addEventListener('change', () => {
        const id = cb.dataset.col as ColId;
        this.colVisible[id] = cb.checked;
        this.saveCols();
        this.renderColHead();
        this.rebuildLadder();
        this.paintVolProfile();
        if (id === 'vol' && cb.checked) void this.loadVolProfile();
      });
    }
  }

  /** Force the rows to be rebuilt on the next snapshot. */
  private rebuildLadder(): void {
    const ladder = this.q<HTMLElement>('.js-ladder');
    delete ladder.dataset.tick;
    if (this.lastSnapshot) this.renderLadder(this.lastSnapshot);
  }

  private applyColWidths(): void {
    const val = this.visibleCols().map((c) => this.colWidths[c.id] + 'px').join(' ');
    this.q<HTMLElement>('.ldr-head').style.gridTemplateColumns = val;
    this.q<HTMLElement>('.js-ladder').style.setProperty('--ladder-cols', val);
    // Histograms mirror around price: left of it anchors right and grows leftward.
    const price = this.visibleCols().findIndex((c) => c.id === 'price');
    this.visibleCols().forEach((c, i) => {
      if (!c.hist) return;
      const left = price >= 0 && i < price;
      for (const cell of this.el.querySelectorAll<HTMLElement>(`.${c.cls}`)) {
        cell.classList.toggle('hist-left', left);
      }
    });
  }

  /** Auto-fits each column via canvas measureText, so it costs no layout reflow even at 999 rows. */
  private autoResizeCols(): void {
    const head = this.q<HTMLElement>('.ldr-head');
    const rows = this.el.querySelectorAll<HTMLElement>('.js-ladder .lrow');
    if (!rows.length) {
      toast('Load a contract first.');
      return;
    }
    const ctx = this.measCtx ?? (this.measCtx = document.createElement('canvas').getContext('2d')!);
    const fontOf = (el: Element) => {
      const s = getComputedStyle(el);
      return `${s.fontWeight} ${s.fontSize} ${s.fontFamily}`;
    };
    const padOf = (el: Element) => {
      const s = getComputedStyle(el);
      return (parseFloat(s.paddingLeft) || 0) + (parseFloat(s.paddingRight) || 0);
    };
    this.visibleCols().forEach((col, c) => {
      // A histogram holds no text, so measuring it would collapse it to nothing.
      if (col.hist) return;
      let max = 0;
      const hc = head.children[c];
      if (hc) {
        ctx.font = fontOf(hc);
        max = ctx.measureText(hc.textContent || '').width + padOf(hc);
      }
      const sample = rows[0].children[c];
      if (sample) {
        ctx.font = fontOf(sample);
        const pad = padOf(sample);
        for (const r of Array.from(rows)) {
          const cell = r.children[c];
          if (!cell) continue;
          const w = ctx.measureText(cell.textContent || '').width + pad;
          if (w > max) max = w;
        }
      }
      this.colWidths[col.id] = Math.max(24, Math.ceil(max) + 1);
    });
    this.saveCols();
    this.applyColWidths();
    this.syncHandles();
  }

  private syncHandles(): void {
    const head = this.q<HTMLElement>('.ldr-head');
    head.querySelectorAll('.col-handle').forEach((h) => h.remove());
    const vis = this.visibleCols();
    vis.forEach((col, i) => {
      const h = document.createElement('div');
      h.className = 'col-handle';
      head.appendChild(h);
      this.positionHandle(h, i);
      h.addEventListener('mousedown', (e) => this.startResize(e, col.id));
    });
  }

  private positionHandle(h: HTMLElement, i: number): void {
    const vis = this.visibleCols();
    let left = 0;
    for (let k = 0; k <= i && k < vis.length; k++) left += this.colWidths[vis[k].id];
    h.style.left = left + 'px';
  }

  private startResize(e: MouseEvent, col: ColId): void {
    e.preventDefault();
    const startX = e.clientX;
    const startW = this.colWidths[col];
    const onMove = (ev: MouseEvent) => {
      this.colWidths[col] = Math.max(24, startW + ev.clientX - startX);
      this.applyColWidths();
      this.el.querySelectorAll<HTMLElement>('.col-handle').forEach((h, k) => this.positionHandle(h, k));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }
}

/** Quotes come back as dollar strings; the ladder speaks cents. */
function compact(v: unknown): string {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return String(Math.round(n));
}

/** Clock label whose precision follows the window: seconds for a minute-long chart, dates for days. */
function fmtClock(t: number, spanMs: number): string {
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, '0');
  if (spanMs <= 5 * 60_000) return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  if (spanMs <= 36 * 3_600_000) return `${p(d.getHours())}:${p(d.getMinutes())}`;
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}h`;
}
